import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  FONTE_TIPO_LABELS,
  PAPEL_FONTE_LABELS,
  STATUS_LABELS,
  formatBRL,
  formatDate,
  type Fonte,
} from '@/pages/painel/lib/radarConstants';

interface OportunidadeResumo {
  id: number;
  titulo: string;
  status: string;
  hop_score: number | null;
  valor_obra: number | null;
  valor_potencial_hop: number | null;
  lucro_potencial: number | null;
  cidade: string | null;
  uf: string | null;
  created_at: string;
}

interface ObraResumo {
  id: number;
  rrt_numero: string | null;
  contratante: string | null;
  segmento: string | null;
  natureza_tecnica: string | null;
  valor_contrato_max: number | null;
  cidade: string | null;
  uf: string | null;
  importado_em: string | null;
}

interface Referencia {
  origem: string;
  papel: string;
  url_especifica: string | null;
  observacao: string | null;
  data_captura: string | null;
}

interface HistoricoItem {
  tipo: string;
  titulo: string;
  data: string;
}

interface Props {
  fonte: Fonte;
  password: string;
  onClose: () => void;
}

export default function RadarFonteDetailModal({ fonte, password, onClose }: Props) {
  const [oportunidades, setOportunidades] = useState<OportunidadeResumo[]>([]);
  const [obras, setObras] = useState<ObraResumo[]>([]);
  const [referencias, setReferencias] = useState<Referencia[]>([]);
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
          body: { action: 'fonte_detalhar', password, fonte_id: fonte.id },
        });
        if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
        const parsed = data as {
          oportunidades?: OportunidadeResumo[];
          obras?: ObraResumo[];
          referencias?: Referencia[];
          historico?: HistoricoItem[];
          error?: string;
        };
        if (parsed?.error) throw new Error(parsed.error);
        setOportunidades(parsed?.oportunidades ?? []);
        setObras(parsed?.obras ?? []);
        setReferencias(parsed?.referencias ?? []);
        setHistorico(parsed?.historico ?? []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erro ao carregar detalhes da fonte.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fonte.id, password]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-3xl max-h-[92vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
              <i className="ri-rss-line text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base">{fonte.nome}</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                  {FONTE_TIPO_LABELS[fonte.tipo] ?? fonte.tipo}
                </span>
                {fonte.url && (
                  <a href={fonte.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#E8610A] hover:underline">
                    {fonte.url}
                  </a>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* KPIs separando valor da obra de potencial HOP */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Oportunidades</p>
                  <p className="text-lg font-bold text-gray-900">{oportunidades.length}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Obras</p>
                  <p className="text-lg font-bold text-gray-900">{obras.length}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Oportunidades quentes</p>
                  <p className={`text-lg font-bold ${oportunidades.filter((o) => (o.hop_score ?? 0) >= 70).length > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {oportunidades.filter((o) => (o.hop_score ?? 0) >= 70).length}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Valor das obras mapeadas</p>
                  <p className="text-lg font-bold text-gray-900">{formatBRL(obras.reduce((s, o) => s + Number(o.valor_contrato_max ?? 0), 0))}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Potencial HOP</p>
                  <p className="text-lg font-bold text-gray-900">
                    {oportunidades.some((o) => o.valor_potencial_hop != null)
                      ? formatBRL(oportunidades.reduce((s, o) => s + Number(o.valor_potencial_hop ?? 0), 0))
                      : 'Não estimado'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Lucro potencial HOP</p>
                  <p className="text-lg font-bold text-green-600">{formatBRL(oportunidades.reduce((s, o) => s + Number(o.lucro_potencial ?? 0), 0))}</p>
                </div>
              </div>

              {/* Oportunidades originadas */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Oportunidades originadas</h4>
                {oportunidades.length === 0 ? (
                  <p className="text-sm text-gray-400">Nenhuma oportunidade vinculada a esta fonte ainda.</p>
                ) : (
                  <div className="space-y-2">
                    {oportunidades.map((o) => (
                      <Link
                        key={o.id}
                        to={`/painel/radar/oportunidades/${o.id}`}
                        className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2.5 hover:border-[#E8610A]/30 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{o.titulo}</p>
                          <p className="text-xs text-gray-500">
                            {[o.cidade, o.uf].filter(Boolean).join(' - ') || '—'} · {STATUS_LABELS[o.status] ?? o.status}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-gray-800">
                            {o.valor_potencial_hop != null ? formatBRL(o.valor_potencial_hop) : 'Não estimado'}
                          </p>
                          {o.valor_obra != null && (
                            <p className="text-[11px] text-gray-400">obra {formatBRL(o.valor_obra)}</p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Obras relacionadas */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Obras relacionadas</h4>
                {obras.length === 0 ? (
                  <p className="text-sm text-gray-400">Nenhuma obra vinculada a esta fonte.</p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {obras.map((o) => (
                      <div key={o.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{o.contratante ?? o.rrt_numero}</p>
                          <p className="text-xs text-gray-500">
                            {o.rrt_numero && <span className="font-mono mr-2">{o.rrt_numero}</span>}
                            {o.segmento ?? '—'} · {[o.cidade, o.uf].filter(Boolean).join(' - ')}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-gray-800 shrink-0">{formatBRL(o.valor_contrato_max)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* URLs / referências */}
              {referencias.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">URLs / referências</h4>
                  <div className="space-y-1.5">
                    {referencias.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap shrink-0 mt-0.5">
                          {PAPEL_FONTE_LABELS[r.papel] ?? r.papel}
                        </span>
                        {r.url_especifica ? (
                          <a href={r.url_especifica} target="_blank" rel="noopener noreferrer" className="text-[#E8610A] hover:underline break-all">
                            {r.url_especifica}
                          </a>
                        ) : (
                          <span className="text-gray-600">{r.observacao ?? '—'}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Histórico */}
              {historico.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Histórico de ocorrências</h4>
                  <div className="space-y-1.5">
                    {historico.slice(0, 30).map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${
                          h.tipo === 'oportunidade' ? 'bg-[#E8610A]/10 text-[#E8610A]' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {h.tipo === 'oportunidade' ? 'Oportunidade' : 'Obra'}
                        </span>
                        <span className="text-gray-700 truncate flex-1">{h.titulo}</span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(h.data)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}